"use client";

import { useLogger } from "@/lib/observability/LoggerContext";
import { Logo } from "@workspace/brand/components/logo";
import { Button } from "@workspace/ui/components/button";
import { useEffect } from "react";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const logger = useLogger("GlobalError");

  useEffect(() => {
    logger.logError("GlobalError", error, {
      digest: error.digest,
      userAgent:
        typeof window !== "undefined" ? window.navigator.userAgent : undefined,
    });
  }, [error, logger]);

  return (
    <div className="bg-cream min-h-screen">
      {/* Hero Background Section */}
      <section className="relative mb-6 h-[368px] w-full overflow-hidden">
        {/* White Background */}
        <div className="bg-brand-100 absolute inset-0" />

        {/* Static Background with Lower Opacity */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-90"
          style={{
            backgroundImage:
              "url('https://localplatform.fly.storage.tigris.dev/assets/liquid_hero_first_frame.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Top Gradient Fade from Cream Background */}
        <div className="from-cream via-cream/70 pointer-events-none absolute left-0 right-0 top-0 h-24 bg-linear-to-b to-transparent" />

        {/* Bottom Gradient Fade to Cream Background */}
        <div className="bg-gradient-t from-cream via-cream/70 pointer-events-none absolute bottom-0 left-0 right-0 h-36 to-transparent" />
      </section>

      {/* Content Container */}
      <div className="relative z-10 -mt-64 flex flex-col items-center justify-center px-4 py-6 md:px-8 md:py-8">
        {/* Title section */}
        <div className="mx-auto mb-6 flex w-full max-w-2xl flex-col items-center gap-4 text-center">
          <Logo symbolOnly className="size-10 md:size-12" />
          <div className="flex flex-col items-center gap-2">
            <h1 className="mb-0 font-serif text-3xl font-bold md:text-4xl">
              Something went wrong
            </h1>
            <p className="text-brand-700 max-w-md text-sm md:text-base">
              We&apos;re sorry, but something unexpected happened. Our team has
              been notified and is working to fix the issue.
            </p>
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-cream-50/70 mb-24 w-full rounded-lg border border-neutral-200/50 px-4 py-6 shadow-md backdrop-blur-xl md:w-[500px] md:px-8 md:py-8">
          <div className="flex flex-col items-center gap-6">
            <div className="text-center">
              <h2 className="text-brand-900 mb-2 text-lg font-semibold">
                Error Details
              </h2>
              <p className="text-brand-600 mb-4 text-sm">
                {process.env.NODE_ENV === "development"
                  ? error.message || "An unexpected error occurred"
                  : "An unexpected error occurred"}
              </p>
              {error.digest && (
                <p className="text-brand-500 font-mono text-xs">
                  Error ID: {error.digest}
                </p>
              )}
            </div>

            <div className="flex w-full flex-col gap-3 sm:flex-row">
              <Button onClick={reset} variant="default" className="flex-1">
                Try Again
              </Button>
              <Button
                onClick={() => (window.location.href = "/")}
                variant="outline"
                className="flex-1"
              >
                Go Home
              </Button>
            </div>

            <div className="text-center">
              <p className="text-brand-500 text-xs">
                If this problem persists, please contact support or try
                refreshing the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
